"use client";

import Image from "next/image";
import React, { useState } from "react";

import VerifiedTick from "@/components/reviews/VerifiedTick";
import { urlForImage } from "@/sanity/lib/image";
import type { ReviewSource } from "@/app/(client)/reviews/page";

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

interface Review {
  _id: string;
  title?: string;
  context?: string;
  tags?: string[];
  stars?: number;
  source?: string;
  verified?: boolean;
  name?: string;
  publishedAt?: string;
  avatar?: { asset?: { _ref?: string }; _type?: string };
  avatarUrl?: string;
  sourceUrl?: string;
}

function StarsDisplay({ stars }: { stars: number }) {
  const n = Math.min(5, Math.max(0, stars));
  return (
    <span className="inline-flex text-amber-400" aria-label={`${n} stars`}>
      {"★".repeat(n)}
      {"☆".repeat(5 - n)}
    </span>
  );
}

interface FeaturedReviewsCarouselProps {
  reviews: Review[];
  reviewSources: ReviewSource[];
}

export default function FeaturedReviewsCarousel({
  reviews,
  reviewSources,
}: FeaturedReviewsCarouselProps) {
  const [index, setIndex] = useState(0);
  const current = reviews[index];
  const hasMultiple = reviews.length > 1;
  const sourceConfig = current?.source
    ? reviewSources.find(
        (s) => slugify(s.name) === current.source?.toLowerCase()
      )
    : null;
  const sourceLogoUrl =
    sourceConfig?.logo &&
    urlForImage(sourceConfig.logo).width(32).height(32).url();

  const goPrev = () => {
    setIndex((i) => (i === 0 ? reviews.length - 1 : i - 1));
  };

  const goNext = () => {
    setIndex((i) => (i === reviews.length - 1 ? 0 : i + 1));
  };

  if (!current) return null;

  return (
    <section
      className="relative w-full overflow-hidden bg-violet-50/50 py-10"
      aria-label="Featured reviews"
    >
      <div className="mx-auto max-w-4xl px-4">
        <article
          className="rounded-2xl border border-violet-200 bg-white p-6 shadow-sm md:p-8"
          data-speakable-review
        >
          <div className="flex items-start gap-4">
            {(current.avatar || current.avatarUrl) && (
              <div className="relative size-14 shrink-0 overflow-hidden rounded-full bg-violet-100">
                <Image
                  src={
                    current.avatar
                      ? urlForImage(current.avatar).width(56).height(56).url()
                      : (current.avatarUrl ?? "")
                  }
                  alt=""
                  width={56}
                  height={56}
                  className="object-cover"
                  unoptimized={Boolean(current.avatarUrl && !current.avatar)}
                />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <StarsDisplay stars={current.stars ?? 0} />
                {sourceLogoUrl ? (
                  <span
                    className="relative flex size-6 shrink-0"
                    aria-label={sourceConfig?.name}
                  >
                    <Image
                      src={sourceLogoUrl}
                      alt=""
                      width={24}
                      height={24}
                      className="object-contain"
                    />
                  </span>
                ) : null}
                {current.verified && <VerifiedTick />}
              </div>
              {current.title && (
                <h3 className="text-xl font-semibold text-violet-950">
                  {current.title}
                </h3>
              )}
              <p className="mt-3 text-violet-800">{current.context}</p>
              {current.tags && current.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {current.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded bg-violet-100 px-1.5 py-0.5 text-xs text-violet-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-4 flex items-center justify-between gap-2">
                <p className="font-medium text-violet-900">— {current.name}</p>
                {current.sourceUrl && (
                  <a
                    href={current.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 rounded p-1.5 text-violet-600 hover:bg-violet-100 hover:text-violet-800"
                    aria-label="View on source"
                  >
                    <svg
                      className="size-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        </article>
      </div>

      {hasMultiple && (
        <div className="mt-6 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={goPrev}
            className="flex size-12 items-center justify-center rounded-full border border-violet-200 bg-white text-violet-700 shadow-sm transition-colors hover:bg-violet-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
            aria-label="Previous review"
          >
            <svg
              className="size-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <span className="text-sm text-violet-700" aria-live="polite">
            {index + 1} / {reviews.length}
          </span>
          <button
            type="button"
            onClick={goNext}
            className="flex size-12 items-center justify-center rounded-full border border-violet-200 bg-white text-violet-700 shadow-sm transition-colors hover:bg-violet-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
            aria-label="Next review"
          >
            <svg
              className="size-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}
